import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useCalculator } from '../../../contexts/CalculatorContext';

/**
 * GlobalSettingsDialog component for global calculator settings
 */
const GlobalSettingsDialog = ({ open: propOpen, onClose: propOnClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isAdmin, isCustomerSuccess } = useAuth();
  const {
    handleApplyGlobalSettings,
    showAdvancedTier,
    showPricesWithoutBonuses,
  } = useCalculator();

  const isOpen = propOpen !== undefined ? propOpen : false;
  const canSeePriceToggle = isAdmin || isCustomerSuccess;

  // Local state for the dialog form
  const [localShowAdvancedTier, setLocalShowAdvancedTier] = useState(showAdvancedTier);
  const [localShowPricesWithoutBonuses, setLocalShowPricesWithoutBonuses] = useState(showPricesWithoutBonuses);

  // Sync local state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalShowAdvancedTier(showAdvancedTier);
      setLocalShowPricesWithoutBonuses(showPricesWithoutBonuses);
    }
  }, [isOpen, showAdvancedTier, showPricesWithoutBonuses]);

  const onClose = () => {
    if (propOnClose) propOnClose();
  };

  const handleApply = () => {
    handleApplyGlobalSettings({
      showAdvancedTier: localShowAdvancedTier,
      showPricesWithoutBonuses: localShowPricesWithoutBonuses,
      hasGlobalSettings: localShowAdvancedTier || localShowPricesWithoutBonuses,
    });
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.spacing(1),
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f9f9f9',
          py: 1.5,
          '& .MuiTypography-root': {
            fontWeight: 500,
            fontSize: 18
          }
        }}
      >
        {t('calculator.globalSettings', 'Global Settings')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localShowAdvancedTier}
                onChange={(e) => setLocalShowAdvancedTier(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#00C853',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#00C853',
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {t('calculator.showAdvancedTier', 'Show Advanced Tier')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('calculator.showAdvancedTierDescription', 'Enable the hidden Advanced tier for Business Phone (UA, Central Asia, Other regions)')}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0 }}
          />
        </Box>

        {canSeePriceToggle && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localShowPricesWithoutBonuses}
                  onChange={(e) => setLocalShowPricesWithoutBonuses(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#00C853',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#00C853',
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t('calculator.showPricesWithoutBonuses', 'Display prices without long-term bonuses')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('calculator.showPricesWithoutBonusesDescription', 'Monthly fee forecast will show prices without 360-day subscription discount')}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            borderRadius: theme.spacing(1),
            px: 3
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            bgcolor: '#00C853',
            '&:hover': {
              bgcolor: '#00A040',
            },
            borderRadius: theme.spacing(1),
            px: 3
          }}
        >
          {t('common.apply', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalSettingsDialog;
